import {
  Box,
  Grid,
  IconButton,
  Modal,
  Paper,
  Table,
  TableBody,
  TableHead,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { DoctorconsoleGrid, OutlinedIconFab } from "../StyledComponents";
import Icon from "../Utils/Icons";

export const AttachmentsFilePreview = ({
  data,
  activeSection,
  handleAttachmentPreviewModalOpen = () => {},
  handleDeleteAttachment = () => {},
  closeAttachmentPreviewModal = () => {},
  attachmentPreviewModalOpen = false,
  selectedAttachmentForPreview = null,
}) => {
  const handleDownloadAttachment = () => {
    if (!selectedAttachmentForPreview) return;
    const link = document.createElement("a");
    link.href = selectedAttachmentForPreview.base64;
    link.download = selectedAttachmentForPreview.name || "attachment";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrintAttachment = () => {
    if (!selectedAttachmentForPreview) return;
    const { base64, type, name } = selectedAttachmentForPreview;

    if (type === "application/pdf") {
      if (base64.startsWith("data:")) {
        const byteString = atob(base64.split(",")[1]);
        const byteArray = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
          byteArray[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        const printWindow = window.open(blobUrl);
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
            URL.revokeObjectURL(blobUrl);
          };
        }
      } else {
        const printWindow = window.open(base64, "_blank");
        if (printWindow) {
          printWindow.onload = () => printWindow.print();
        }
      }
    } else {
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(
          "<html><head><title>" +
            (name || "image") +
            '</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh">' +
            '<img src="' +
            base64 +
            '" style="max-width:100%;max-height:100vh;" onload="window.print();" />' +
            "</body></html>"
        );
        printWindow.document.close();
      }
    }
  };

  if (!data || data.length === 0) return null;

  return (
    <>
      {activeSection && data.length === 0 ? (
        <DoctorconsoleGrid item xs={12}>
          <DoctorconsoleGrid container direction="column" alignItems="center">
            <img
              src="/img/medications.jpeg"
              alt="Placeholder"
              height="100"
              width="100"
              style={{ borderRadius: "50%" }}
            />
            <Typography variant="body1" sx={{ marginTop: 1 }}>
              Selected Files will be shown here
            </Typography>
          </DoctorconsoleGrid>
        </DoctorconsoleGrid>
      ) : data.length > 0 ? (
        <DoctorconsoleGrid item xs={12}>
          <DoctorconsoleGrid
            container
            sx={{
              padding: 1,
              maxHeight: "150px",
              overflowY: "auto",
              borderRadius: 2,
            }}
          >
            {data.length > 0 &&
              data.map((file, index) => (
                <Grid
                  key={index}
                  container
                  item
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    backgroundColor: "#E9FAF9",
                    "&:hover": {
                      backgroundColor: "#5caff4ff",
                    },
                    borderRadius: 2,
                    marginBottom: 1,
                    padding: 1,
                    cursor: activeSection ? "pointer" : "default",
                  }}
                >
                  <Grid
                    item
                    xs={10}
                    onClick={() =>
                      activeSection && handleAttachmentPreviewModalOpen(file)
                    }
                  >
                    <Typography variant="body2">{file.name}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    {activeSection && file && (
                      <OutlinedIconFab
                        onClick={() => handleDeleteAttachment(index)}
                      >
                        <Icon name="deleteInTable" size={32} />
                      </OutlinedIconFab>
                    )}
                  </Grid>
                </Grid>
              ))}
          </DoctorconsoleGrid>
        </DoctorconsoleGrid>
      ) : null}

      {activeSection && (
        <Modal
          open={attachmentPreviewModalOpen}
          onClose={closeAttachmentPreviewModal}
        >
          <Box className="modal-box">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 12px",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <h1
                className="console-heading"
                style={{ margin: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {selectedAttachmentForPreview !== null &&
                  selectedAttachmentForPreview.name}
              </h1>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                {selectedAttachmentForPreview !== null && (
                  <>
                    <Tooltip title="Download">
                      <IconButton
                        onClick={handleDownloadAttachment}
                        sx={{
                          color: "#125386",
                          backgroundColor: "#e3f2fd",
                          "&:hover": { backgroundColor: "#bbdefb" },
                        }}
                      >
                        <DownloadOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Print">
                      <IconButton
                        onClick={handlePrintAttachment}
                        sx={{
                          color: "#125386",
                          backgroundColor: "#e3f2fd",
                          "&:hover": { backgroundColor: "#bbdefb" },
                        }}
                      >
                        <PrintOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                <IconButton onClick={closeAttachmentPreviewModal}>
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
            <div style={{ padding: "16px", flex: 1, overflow: "auto" }}>
              {selectedAttachmentForPreview !== null &&
              selectedAttachmentForPreview.type === "application/pdf" ? (
                <iframe
                  src={
                    selectedAttachmentForPreview !== null &&
                    selectedAttachmentForPreview.base64 + "#toolbar=0"
                  }
                  title={
                    selectedAttachmentForPreview !== null &&
                    selectedAttachmentForPreview.name
                  }
                  width="100%"
                  height="200%"
                  style={{ border: "none" }}
                ></iframe>
              ) : (
                <img
                  src={
                    selectedAttachmentForPreview !== null &&
                    selectedAttachmentForPreview.base64 + "#toolbar=0"
                  }
                  alt={
                    selectedAttachmentForPreview !== null &&
                    selectedAttachmentForPreview.name
                  }
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              )}
            </div>
          </Box>
        </Modal>
      )}
    </>
  );
};
